// OMNI Design System Input Recipe for Chakra UI v3
// Based on OMNI CSS form input styles

import { defineRecipe } from '@chakra-ui/react';

export const inputRecipe = defineRecipe({
  className: 'omni-input',
  base: {
    // Base input styles from OMNI
    appearance: 'none',
    borderRadius: 'base', // 8px (--radius)
    border: '1px solid', // --form-input-border: 1px solid var(--color-pale-grey-three)
    borderColor: 'border.default',
    backgroundColor: 'bg.surface',
    color: 'fg.default',
    fontFamily: 'body',
    fontSize: 'md', // 14px (--size-4)
    fontWeight: 'normal',
    lineHeight: 'normal',
    outline: 'transparent',
    position: 'relative',
    transition: 'all 200ms ease',
    width: '100%',
    minWidth: '0',

    // Placeholder styling
    _placeholder: {
      color: 'fg.placeholder',
      opacity: 1,
    },

    // Focus styles with OMNI focus treatment
    _focus: {
      borderColor: 'brand.500',
      boxShadow: 'bulma-focus', // 0 0 0 0.125em rgba(0, 161, 210, 0.25)
      outline: 'none',
    },

    // Hover styles
    _hover: {
      borderColor: 'border.emphasis',
    },

    // Disabled styles
    _disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      backgroundColor: 'bg.muted',
      borderColor: 'border.subtle',
    },

    // Invalid/Error styles
    _invalid: {
      borderColor: 'red.500',
      boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)', // OMNI error shadow
    },

    // Read-only styles
    _readOnly: {
      backgroundColor: 'bg.subtle',
      cursor: 'default',
    },
  },

  variants: {
    // Size variants
    size: {
      xs: {
        fontSize: '2xs', // 11px
        height: '6', // 24px
        paddingX: '2', // 8px
        paddingY: '1', // 4px
      },
      sm: {
        fontSize: 'xs', // 12px
        height: '8', // 32px
        paddingX: '3', // 12px
        paddingY: '2', // 8px
      },
      md: {
        fontSize: 'md', // 14px (OMNI default)
        height: '10', // 40px
        paddingX: '4', // 16px
        paddingY: '2.5', // 10px
      },
      lg: {
        fontSize: 'lg', // 16px
        height: '12', // 48px
        paddingX: '5', // 20px
        paddingY: '3', // 12px
      },
      xl: {
        fontSize: 'xl', // 18px
        height: '14', // 56px
        paddingX: '6', // 24px
        paddingY: '4', // 16px
      },
    },

    // Visual variants
    variant: {
      // Default OMNI style
      outline: {
        borderColor: 'border.default',
        backgroundColor: 'bg.surface',

        _focus: {
          borderColor: 'brand.500',
          boxShadow: 'bulma-focus',
        },

        _invalid: {
          borderColor: 'red.500',
          boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
        },
      },

      // Filled variant
      filled: {
        borderColor: 'transparent',
        backgroundColor: 'bg.subtle',

        _focus: {
          borderColor: 'brand.500',
          backgroundColor: 'bg.surface',
          boxShadow: 'bulma-focus',
        },

        _hover: {
          backgroundColor: 'bg.muted',
        },

        _invalid: {
          borderColor: 'red.500',
          backgroundColor: 'red.50',
          boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
        },
      },

      // Flushed variant (underlined)
      flushed: {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        borderRadius: '0',
        borderBottomWidth: '2px',
        borderBottomColor: 'border.default',
        paddingX: '0',

        _focus: {
          borderBottomColor: 'brand.500',
          boxShadow: 'none',
        },

        _invalid: {
          borderBottomColor: 'red.500',
          boxShadow: 'none',
        },
      },

      // Unstyled variant
      unstyled: {
        border: 'none',
        backgroundColor: 'transparent',
        borderRadius: '0',
        height: 'auto',
        paddingX: '0',
        paddingY: '0',

        _focus: {
          boxShadow: 'none',
        },
      },
    },

    // State-based styling
    state: {
      // Success state
      success: {
        borderColor: 'green.500',

        _focus: {
          borderColor: 'green.500',
          boxShadow: '0 0 0 0.125em rgba(44, 196, 173, 0.25)',
        },
      },

      // Warning state
      warning: {
        borderColor: 'yellow.500',

        _focus: {
          borderColor: 'yellow.500',
          boxShadow: '0 0 0 0.125em rgba(254, 216, 130, 0.25)',
        },
      },

      // Error state (same as invalid)
      error: {
        borderColor: 'red.500',

        _focus: {
          borderColor: 'red.500',
          boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
        },
      },
    },
  },

  compoundVariants: [
    // Filled variant with success state
    {
      variant: 'filled',
      state: 'success',
      css: {
        backgroundColor: 'green.50',
        borderColor: 'green.500',
      },
    },

    // Filled variant with warning state
    {
      variant: 'filled',
      state: 'warning',
      css: {
        backgroundColor: 'yellow.50',
        borderColor: 'yellow.500',
      },
    },

    // Filled variant with error state
    {
      variant: 'filled',
      state: 'error',
      css: {
        backgroundColor: 'red.50',
        borderColor: 'red.500',
      },
    },

    // Flushed variant with states
    {
      variant: 'flushed',
      state: 'success',
      css: {
        borderBottomColor: 'green.500',
      },
    },

    {
      variant: 'flushed',
      state: 'warning',
      css: {
        borderBottomColor: 'yellow.500',
      },
    },

    {
      variant: 'flushed',
      state: 'error',
      css: {
        borderBottomColor: 'red.500',
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});

// Textarea recipe (extends input)
export const textareaRecipe = defineRecipe({
  className: 'omni-textarea',
  base: {
    // Inherit all base styles from input
    appearance: 'none',
    borderRadius: 'base',
    border: '1px solid',
    borderColor: 'border.default',
    backgroundColor: 'bg.surface',
    color: 'fg.default',
    fontFamily: 'body',
    fontSize: 'md',
    fontWeight: 'normal',
    lineHeight: 'normal',
    outline: 'transparent',
    position: 'relative',
    transition: 'all 200ms ease',
    width: '100%',
    minWidth: '0',
    resize: 'vertical',
    minHeight: '20', // 80px minimum height
    paddingY: '3', // Slightly more vertical padding for textarea

    _placeholder: {
      color: 'fg.placeholder',
      opacity: 1,
    },

    _focus: {
      borderColor: 'brand.500',
      boxShadow: 'bulma-focus',
      outline: 'none',
    },

    _hover: {
      borderColor: 'border.emphasis',
    },

    _disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      backgroundColor: 'bg.muted',
      borderColor: 'border.subtle',
      resize: 'none',
    },

    _invalid: {
      borderColor: 'red.500',
      boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
    },

    _readOnly: {
      backgroundColor: 'bg.subtle',
      cursor: 'default',
      resize: 'none',
    },
  },

  variants: {
    size: {
      sm: {
        fontSize: 'sm',
        paddingX: '3',
        paddingY: '2',
        minHeight: '16', // 64px
      },
      md: {
        fontSize: 'md',
        paddingX: '4',
        paddingY: '3',
        minHeight: '20', // 80px
      },
      lg: {
        fontSize: 'lg',
        paddingX: '5',
        paddingY: '4',
        minHeight: '24', // 96px
      },
    },

    variant: {
      outline: {
        borderColor: 'border.default',
        backgroundColor: 'bg.surface',
      },
      filled: {
        borderColor: 'transparent',
        backgroundColor: 'bg.subtle',
      },
      flushed: {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        borderRadius: '0',
        borderBottomWidth: '2px',
        borderBottomColor: 'border.default',
        paddingX: '0',
      },
      unstyled: {
        border: 'none',
        backgroundColor: 'transparent',
        borderRadius: '0',
        paddingX: '0',
        paddingY: '0',
      },
    },

    resize: {
      none: { resize: 'none' },
      both: { resize: 'both' },
      horizontal: { resize: 'horizontal' },
      vertical: { resize: 'vertical' },
    },
  },

  defaultVariants: {
    size: 'md',
    variant: 'outline',
    resize: 'vertical',
  },
});
